import { TOKEN_IWrapTranslations, type IWrapTranslations } from "@/services/WrapTranslations";
import { inject, injectable } from "tsyringe";
import { ProjectAccessStore, TOKEN_ProjectAccessStore } from "@/views/utility-stores/ProjectAccessStore";
import { makeAutoObservable } from "mobx";
import { TOKEN_INavigationRedirect, type INavigationRedirect } from "@/services/NavigationRedirect";
import GlobalMenuStore, { TOKEN_GlobalMenuStore } from "./GlobalMenuStore";
import { TOKEN_IProjectWorldWriter, type IProjectWorldWriter } from "@/services/ProjectWorldWriter";
import { ReplayStore, TOKEN_ReplayStore } from "./utility-stores/ReplayStore";
import { AvailableModalDialogs, ModalDialogStore, TOKEN_ModalDialogStore } from "./modalDialogs/ModalDialogStore";
import ThemeStore, { TOKEN_ThemeStore } from "./utility-stores/ThemeStore";
import { GameContainerStore, TOKEN_GameContainerStore } from "./GameContainerStore";
import RunningActionStore, { TOKEN_RunningActionStore } from "./utility-stores/RunningActionStore";
import { TOKEN_IServerCalls, type IServerCalls } from "@/services/ServerCalls";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "@/services/ProjectWorldReader";
import { TOKEN_IWrapAuth0, type IWrapAuth0 } from "@/services/WrapAuth0";
import { TOKEN_IProjectBus, type IProjectBus } from "@/services/ProjectBus";

export const TOKEN_ColorSquareMenuStore = Symbol("ColorSquareMenuStore");

@injectable()
export class ColorSquareMenuStore {

    private _isLoggedIn: boolean = false;

    openMyProjectsDialog = () => {
        this.globalMenuStore.close();
        this.modalDialogStore.openDialog(AvailableModalDialogs.MyProjects, this.t.t('my_projects'), this.t.t('my_projects_description'));
    }
    get isLoggedIn(): boolean {
        return this._isLoggedIn;
    }
    loginOrLogout = async () => {
        if (this.isLoggedIn) {
            await this.wrapAuth0.logout();
        } else {
            await this.wrapAuth0.loginWithRedirect();
        }
    }
    openShareLinksDialog = () => {
        this.globalMenuStore.close();
        this.modalDialogStore.openDialog(AvailableModalDialogs.ShareLinksList, this.t.t('share_links'), this.t.t('share_links_description'));
    }
    openUsersDialog = () => {
        this.globalMenuStore.close();
        this.modalDialogStore.openDialog(AvailableModalDialogs.ViewAccessList, this.t.t('active_users'), this.t.t('active_users_description'));
    }
    openShareDialog = () => {
        this.globalMenuStore.close();
        this.modalDialogStore.openDialog(AvailableModalDialogs.CreateShareLink, this.t.t('generate_share_link'), this.t.t('generate_share_link_description'));
    }
    playHistory = async () => {
        if (this.replayStore.isInHistoryMode) {
            this.replayStore.stopReplay();
        } else {
            this.gameContainerStore.clearAllColor();
            await this.replayStore.replayEvents();
        }
    }

    get isThemeLight(): boolean | undefined {
        return this.themeStore.currentTheme === "light";
    }
    get isThemeDark(): boolean | undefined {
        return this.themeStore.currentTheme === "dark";
    }
    get isThemeSystem(): boolean | undefined {
        return this.themeStore.currentTheme === "system";
    }
    setThemeSystem = () => { this.themeStore.setTheme('system'); };
    setThemeLight = () => { this.themeStore.setTheme('light'); };
    setThemeDark = () => { this.themeStore.setTheme('dark'); };

    get playHistoryText(): string {
        if (this.projectAccessStore.isInHistoryMode) {
            return this.t.t('stop_history');
        } else {
            return this.t.t('play_history');
        }
    }
    forkProject = async () => {
        await this.navigationRedirect.forkProject(this.projectAccessStore.currentProjectId!, true);
    }
    deleteProject = async () => {
        if (!confirm(this.t.t('confirm_delete_project'))) return;
        const project = await this.projectWorldReader.mustGet(this.projectAccessStore.currentProjectId!);
        if (project.projectMetadata.userOptInForServer) {
            await this.serverCalls.deleteProject(this.projectAccessStore.currentProjectId!);
        }
        await this.projectWorldWriter.deleteProjectWorld(this.projectAccessStore.currentProjectId!);
        await this.navigationRedirect.checkAndRedirect(true);
    }
    createNewProject16 = async () => { await this.createNewProject(16); }
    createNewProject32 = async () => { await this.createNewProject(32); }
    createNewProject64 = async () => { await this.createNewProject(64); }
    createNewProject128 = async () => { await this.createNewProject(128); }
    createNewProject256 = async () => { await this.createNewProject(256); }

    private async createNewProject(gridSize: number) {
        await this.runningActionStore.manageAction(async () => {
            await this.navigationRedirect.createNewProject(gridSize);
        }, true, this.t.t("creating_new_project"));
    }

    get canShare() {
        return this.projectAccessStore.isOwner;
    }

    get theme() {
        return this.themeStore.currentTheme;
    }

    constructor(
        @inject(TOKEN_ModalDialogStore) private modalDialogStore: ModalDialogStore,
        @inject(TOKEN_ProjectAccessStore) private projectAccessStore: ProjectAccessStore,
        @inject(TOKEN_GlobalMenuStore) private globalMenuStore: GlobalMenuStore,
        @inject(TOKEN_ReplayStore) private replayStore: ReplayStore,
        @inject(TOKEN_ThemeStore) private themeStore: ThemeStore,
        @inject(TOKEN_GameContainerStore) private gameContainerStore: GameContainerStore,
        @inject(TOKEN_RunningActionStore) private runningActionStore: RunningActionStore,
        @inject(TOKEN_IWrapTranslations) private t: IWrapTranslations,
        @inject(TOKEN_INavigationRedirect) private navigationRedirect: INavigationRedirect,
        @inject(TOKEN_IProjectWorldWriter) private projectWorldWriter: IProjectWorldWriter,
        @inject(TOKEN_IProjectWorldReader) private projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_IServerCalls) private serverCalls: IServerCalls,
        @inject(TOKEN_IWrapAuth0) private wrapAuth0: IWrapAuth0,
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
    ) {
        makeAutoObservable(this);

        projectBus.subscribe_authenticationChanged(async (data) => {
            this._isLoggedIn = data.isAuthenticated;
        });
    }
}